/**
 * warpStreak.js — ワープストリーク + スターフィールド
 *
 * position: fixed の Canvas を body に追加し、
 * mix-blend-mode: screen でコンテンツに重ねる。
 * スクロール速度に連動してストリークが加速する。
 */

const rand = (min, max) => Math.random() * (max - min) + min;
const CYAN  = '0, 212, 255';
const WHITE = '255, 255, 255';
const STAR_COUNT   = 110;
const STREAK_COUNT = 90;

export default {
  init() {
    const canvas = document.createElement('canvas');
    canvas.id = 'js-warp-canvas';

    // CSS に任せず JS でレイアウトを完全制御
    // （canvas の width/height 属性が CSS を上書きするため）
    Object.assign(canvas.style, {
      position:      'fixed',
      inset:         '0',
      pointerEvents: 'none',
      zIndex:        '2',
      mixBlendMode:  'screen',
      opacity:       '0.55',
    });

    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');

    let W = 0, H = 0;
    const resize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
      // デスクトップでは少し濃く
      canvas.style.opacity = window.innerWidth >= 992 ? '0.7' : '0.55';
    };
    resize();
    window.addEventListener('resize', resize);

    // prefers-reduced-motion 対応
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      canvas.style.display = 'none';
      return;
    }

    // ── スクロール速度の検出 ─────────────────────────
    let lastY     = window.scrollY;
    let velocity  = 0;   // 現在の滑らか速度（0〜1）
    let velTarget = 0;   // 生の目標速度

    window.addEventListener('scroll', () => {
      const dy = Math.abs(window.scrollY - lastY);
      lastY     = window.scrollY;
      velTarget = Math.min(dy / 55, 1.0);
    }, { passive: true });

    // ── スター生成 ──────────────────────────────────
    const makeStar = () => ({
      x:     rand(0, window.innerWidth),
      y:     rand(0, window.innerHeight),
      r:     rand(0.2, 1.5),
      alpha: rand(0.1, 0.55),
      vy:    rand(0.06, 0.28),
    });
    const stars = Array.from({ length: STAR_COUNT }, makeStar);

    // ── ストリーク生成 ──────────────────────────────
    const makeStreak = () => ({
      angle:   rand(0, Math.PI * 2),
      dist:    rand(0, Math.max(window.innerWidth, window.innerHeight) * 0.5),
      speed:   rand(1.5, 4.5),
      baseLen: rand(28, 90),
      alpha:   0,
      color:   Math.random() > 0.38 ? CYAN : WHITE,
      lw:      rand(0.25, 1.0),
    });
    const streaks = Array.from({ length: STREAK_COUNT }, makeStreak);

    // ── 描画ループ ───────────────────────────────────
    const tick = () => {
      requestAnimationFrame(tick);

      // 速度を滑らかに追従・減衰
      velocity  += (velTarget - velocity)  * 0.10;
      velTarget *= 0.87;

      ctx.clearRect(0, 0, W, H);

      const cx = W / 2;
      const cy = H / 2;
      const vn = velocity; // 0〜1

      // ── Stars ──────────────────────────────────────
      stars.forEach(s => {
        // スクロール時は速く流れる
        s.y += s.vy + vn * 6;
        if (s.y > H + 2) {
          s.y = -2;
          s.x = rand(0, W);
        }

        // スクロール中は明るく・大きく
        const a = s.alpha * (0.45 + vn * 0.7);
        const r = s.r * (1 + vn * 0.6);

        ctx.beginPath();
        ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${a})`;
        ctx.fill();
      });

      // ── Streaks ─────────────────────────────────────
      streaks.forEach(sk => {
        // 速度が上がるほど出現
        sk.alpha += vn > 0.04 ? 0.07 : -0.035;
        sk.alpha  = Math.max(0, Math.min(1, sk.alpha));
        if (sk.alpha < 0.01) return;

        // 速度に比例して移動
        sk.dist += sk.speed * (1 + vn * 9);

        const maxDist = Math.hypot(W, H) * 0.65;
        if (sk.dist > maxDist) {
          Object.assign(sk, makeStreak());
          sk.dist  = rand(0, 50);
          sk.alpha = 0;
          return;
        }

        // ストリークの長さもスクロール速度で伸びる
        const len = sk.baseLen * (0.35 + vn * 3.0);
        const x1  = cx + Math.cos(sk.angle) * sk.dist;
        const y1  = cy + Math.sin(sk.angle) * sk.dist;
        const x0  = cx + Math.cos(sk.angle) * Math.max(0, sk.dist - len);
        const y0  = cy + Math.sin(sk.angle) * Math.max(0, sk.dist - len);

        // 先端ほど明るいグラデーション
        const grad = ctx.createLinearGradient(x0, y0, x1, y1);
        const peakAlpha = sk.alpha * Math.min(0.9, vn * 2.2 + 0.12);
        grad.addColorStop(0, `rgba(${sk.color},0)`);
        grad.addColorStop(1, `rgba(${sk.color},${peakAlpha})`);

        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.strokeStyle = grad;
        ctx.lineWidth   = sk.lw;
        ctx.stroke();
      });
    };

    tick();
  },
};
